/**
 * Server Hardware Info Panel
 * Displays system hardware details fetched from /api/health/system
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Server, RefreshCw, Cpu, MemoryStick, HardDrive, Network, Monitor } from 'lucide-react';
import { Button } from '../ui/Button';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

interface SystemInfo {
  hostname: string;
  os: { name: string; platform: string; arch: string; release: string };
  hardware: { manufacturer: string; model: string; serial: string };
  cpu: { model: string; cores: number; speed: number };
  memory: { totalGB: number; freeGB: number; usedPercent: number };
  disks: { drive: string; totalGB: number; freeGB: number; freePercent: number }[];
  network: { name: string; mac: string; ipv4: string; ipv6: string }[];
  uptime: { days: number; hours: number; minutes: number };
}

function DiskBar({ disk }: { disk: SystemInfo['disks'][0] }) {
  const usedPercent = 100 - disk.freePercent;
  const barColor = usedPercent > 90 ? 'bg-red-500' : usedPercent > 75 ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-mono text-slate-300 w-6 flex-shrink-0">{disk.drive}</span>
      <div className="flex-1 h-2.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${usedPercent}%` }} />
      </div>
      <span className="text-[11px] font-mono text-slate-400 w-36 text-right flex-shrink-0">
        {disk.freePercent}% free ({disk.freeGB} GB / {disk.totalGB} GB)
      </span>
    </div>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div className="bg-slate-900/60 rounded-xl border border-slate-800/60 p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <div className="text-sm font-bold text-slate-100 font-mono truncate mt-0.5">{value}</div>
        {sub && <div className="text-[11px] text-slate-500 truncate mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function SkeletonBlock({ className }: { className?: string }) {
  return <div className={`bg-slate-800/50 rounded-lg animate-pulse ${className || ''}`} />;
}

export function ServerInfoPanel(): React.ReactElement {
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf(API_ENDPOINTS.health.system, { signal: AbortSignal.timeout(10_000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Server returned non-JSON response');
      const data: SystemInfo = await res.json();
      setInfo(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch system info');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  // Loading skeleton
  if (loading && !info) {
    return (
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <SkeletonBlock className="h-7 w-48" />
          <SkeletonBlock className="h-8 w-24" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <SkeletonBlock key={i} className="h-20" />)}
        </div>
        <SkeletonBlock className="h-4 w-full" />
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <SkeletonBlock key={i} className="h-3" />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error && !info) {
    return (
      <div className="bg-red-950/20 border border-red-800/40 rounded-2xl p-5 flex items-center gap-3">
        <Server size={18} className="text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-300">Failed to load server hardware info</p>
          <p className="text-xs text-red-400/60 mt-0.5">{error}</p>
        </div>
        <Button variant="secondary" size="sm" onClick={fetchInfo} className="ml-auto flex-shrink-0">
          Retry
        </Button>
      </div>
    );
  }

  if (!info) return <></>;

  const shortCpu = info.cpu.model
    .replace(/\(R\)/g, '')
    .replace(/\(TM\)/g, '')
    .replace(/CPU\s+/i, '')
    .replace(/\s+@\s+.+/, '')
    .trim();

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800/60">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center">
            <Server size={18} className="text-slate-950" />
          </div>
          <div>
            <h3 className="text-sm font-black uppercase tracking-[0.15em] text-slate-200">
              Server <span className="text-accent">Hardware</span>
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5">S/N: {info.hardware.serial}</p>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={fetchInfo}
          disabled={loading}
          leftIcon={<RefreshCw size={13} className={loading ? 'animate-spin' : ''} />}
        >
          Refresh
        </Button>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<Monitor size={16} className="text-accent" />}
            label="Hostname"
            value={info.hostname}
            sub={`${info.hardware.manufacturer} ${info.hardware.model}`.trim().replace('Unknown Unknown', 'Unknown')}
          />
          <StatCard
            icon={<Cpu size={16} className="text-accent" />}
            label="Processor"
            value={`${shortCpu}`}
            sub={`${info.cpu.cores} cores @ ${info.cpu.speed} MHz`}
          />
          <StatCard
            icon={<MemoryStick size={16} className="text-accent" />}
            label="Memory"
            value={`${info.memory.totalGB} GB`}
            sub={`${info.memory.freeGB} GB free (${info.memory.usedPercent}% used)`}
          />
          <StatCard
            icon={<HardDrive size={16} className="text-accent" />}
            label="Storage"
            value={`${info.disks.length} Drive${info.disks.length !== 1 ? 's' : ''}`}
            sub={`${info.disks.reduce((s, d) => s + d.totalGB, 0).toFixed(0)} GB total`}
          />
        </div>

        {/* OS & Uptime row */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-400 px-1">
          <span>
            <span className="text-slate-500 uppercase tracking-wider text-[10px] mr-1.5">OS</span>
            <span className="font-mono text-slate-300">{info.os.name}</span>
            <span className="text-slate-600 ml-1">({info.os.arch})</span>
          </span>
          <span className="text-slate-700">|</span>
          <span>
            <span className="text-slate-500 uppercase tracking-wider text-[10px] mr-1.5">Uptime</span>
            <span className="font-mono text-slate-300">{info.uptime.days}d {info.uptime.hours}h {info.uptime.minutes}m</span>
          </span>
        </div>

        {/* Drives */}
        {info.disks.length > 0 && (
          <div className="space-y-2.5">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 px-1">Drives</h4>
            <div className="space-y-2">
              {info.disks.map(d => <DiskBar key={d.drive} disk={d} />)}
            </div>
          </div>
        )}

        {/* Network */}
        {info.network.length > 0 && (
          <div className="space-y-2.5">
            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 px-1">Network</h4>
            <div className="space-y-1.5">
              {info.network.map((n, i) => (
                <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs px-1">
                  <div className="flex items-center gap-1.5">
                    <Network size={12} className="text-slate-600" />
                    <span className="text-slate-300 font-medium truncate max-w-[200px]">{n.name}</span>
                  </div>
                  <span className="font-mono text-slate-500 text-[11px]">{n.mac}</span>
                  {n.ipv4 && <span className="font-mono text-emerald-400/80 text-[11px]">{n.ipv4}</span>}
                  {n.ipv6 && <span className="font-mono text-slate-600 text-[10px] truncate max-w-[240px]">{n.ipv6}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
